import React, { createContext, useState, useEffect, useMemo, useCallback, FC } from "react"
import { CustomFieldsContextState, CustomFieldServiceData, CustomFieldTypeFromService, FIELD_STATEMENT_OPERATORS } from './CustomFieldTypes'

export const BANNED_FIELD_TYPES = ['Name', 'Multiline', 'Zip', 'Phone', 'HireLocation', 'PreferredLocation']
export const BANNED_STANDARD_JOB_FIELDS = ['-19']
export const BANNED_STANDARD_CANDIDATE_FIELDS = ['-36']

const defaultState: CustomFieldsContextState = {
    customFields: {
        candidateFields: [],
        jobFields: [],
    },
    loading: true,
    error: '',
    // @ts-ignore
    getCandidateFieldType: () => {},
    // @ts-ignore
    getJobFieldType: () => {}
}

export const CustomFieldContext = createContext<CustomFieldsContextState>(defaultState)

const { Provider: ContextProvider, Consumer } = CustomFieldContext

export const CustomFieldsContextConsumer = Consumer

interface CustomFieldsProviderProps {
    offerService: any
}
export const CustomFieldsProvider: FC<CustomFieldsProviderProps> = ({ offerService, children }) => {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [customFields, setCustomFields] = useState(defaultState.customFields)

    useEffect(() => {
        let doCancel = false

        const runAsync = async () => {
            if (doCancel) return

            setLoading(true)
            setError('')
            setCustomFields(defaultState.customFields)
    
            try {
                const returnedOfferFormFields: CustomFieldServiceData = await offerService.getOfferFormFields()

                const newCustomFieldsData = {
                    candidateFields: filterCustomFields(returnedOfferFormFields.candStandard),
                    jobFields: filterCustomFields(returnedOfferFormFields.jobStandard)
                }
                
                if (!doCancel) {
                    setLoading(false)
                    setCustomFields(newCustomFieldsData)
                }
            } catch(e) {
                if (!doCancel) {
                    setError('Failed to fetch data')
                    setLoading(false)
                }
            }

        }

        runAsync()
        
        return () => doCancel = true
    }, [])

    const candidateFieldMap = useMemo(() => {
        const map: {
            [key: string]: CustomFieldTypeFromService
        } = {}

        for(const val of customFields.candidateFields) {
            map[val.fieldEId] = val
        }

        return map
    }, [customFields.candidateFields])
    const jobFieldsMap = useMemo(() => {
        const map: {
            [key: string]: CustomFieldTypeFromService
        } = {}

        for(const val of customFields.jobFields) {
            map[val.fieldEId] = val
        }

        return map
    }, [customFields.jobFields])

    const getCandidateField = useCallback((fieldEId: string): CustomFieldTypeFromService => {
        const retrievedField = candidateFieldMap[fieldEId]

        if (retrievedField) {
            return retrievedField
        } else {
            return null
        }
    }, [candidateFieldMap])

    const getJobField = useCallback((fieldEId: string): CustomFieldTypeFromService => {
        const retrievedField = jobFieldsMap[fieldEId]

        if (retrievedField) {
            return retrievedField
        } else {
            return null
        }
    }, [jobFieldsMap])

    const value = useMemo(() => ({
        customFields,
        loading,
        error,
        getCandidateField,
        getJobField,
    }), [
        customFields.candidateFields,
        customFields.jobFields,
        loading,
        error,
        getCandidateField,
        getJobField,
    ])

    return (
        <ContextProvider value={value}>
            {children}
        </ContextProvider>
    )
}

function filterCustomFields(fields: { [key: string]: Array<CustomFieldTypeFromService> }): Array<CustomFieldTypeFromService> {
    const bannedTypesSet = new Set(BANNED_FIELD_TYPES)
    const bannedJobFieldSet = new Set(BANNED_STANDARD_JOB_FIELDS)
    const bannedCandidateFieldSet = new Set(BANNED_STANDARD_CANDIDATE_FIELDS)

    const availableFieldTypes = Object.keys(FIELD_STATEMENT_OPERATORS)
    const availableFieldTypeSet = new Set(availableFieldTypes)

    const filteredFields = []

    for(const [key, customFields] of Object.entries(fields)) {
        if (key === 'elements') continue

        for(const customField of customFields) {
            if (customField.fieldEId === '0') continue
            if (customField.objectType === 'Job' && bannedJobFieldSet.has(customField.fieldEId))  continue
            if (customField.objectType === 'Candidate' && bannedCandidateFieldSet.has(customField.fieldEId))  continue
            if (bannedTypesSet.has(customField.fieldType)) continue
            if (!availableFieldTypeSet.has(customField.fieldType)) continue

            filteredFields.push(customField)
        }
    }

    return filteredFields
}
